/**
 * Outbound Caller ID pool (a.k.a. Number Pool).
 *
 * Uses `AdminListScaffold` for the same list-page chrome as every other
 * telephony-management tab. The pool endpoint returns `{ numbers: [...] }`
 * (not `{ items }`), so `responseKey="numbers"` is passed to the scaffold.
 *
 * Backend: apps/api/src/routes/admin/number-pool/
 *   GET    /admin/number-pool      -> { numbers: [...] }
 *   POST   /admin/number-pool      -> 201 + row
 *   PATCH  /admin/number-pool/:id  -> 200 + row
 *   DELETE /admin/number-pool/:id  -> 204
 */
import type { JSX } from 'react'

import { AdminListScaffold } from '../../components/admin/AdminListScaffold.js'
import SummaryCards from '../../components/admin/SummaryCards.js'
import type { ColumnDef } from '../../components/admin/ResourceTable.js'
import { formatDateTime } from '../../utils/formatDate.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { NumberPoolDrawer, type PoolNumber } from '../../components/admin/number-pool/NumberPoolDrawer.js'

/** Usage bar turns amber past this share of the cap, red once at it. */
const USAGE_WARN_RATIO = 0.8

/** Number in monospace, with its scope underneath. */
function numberCell(r: PoolNumber): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="truncate font-mono text-[13px] font-semibold text-gray-900">
        {r.phoneNumber}
      </div>
      <div className="text-xs text-gray-500">
        {r.campaignId ? 'Campaign-specific' : 'Shared across campaigns'}
      </div>
    </div>
  )
}

function countryCell(r: PoolNumber): JSX.Element {
  return (
    <span className="inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-inset ring-slate-200">
      {r.countryCode}
    </span>
  )
}

/**
 * Usage today as "n / cap" over a proportional bar. Surfacing this matters
 * operationally: a number silently at its cap is skipped by the picker,
 * which otherwise looks like "no caller ID".
 */
function usageCell(r: PoolNumber): JSX.Element {
  const ratio = r.dailyCap > 0 ? Math.min(r.dailyCallCount / r.dailyCap, 1) : 0
  let barTone = 'bg-emerald-500'
  if (ratio >= 1) barTone = 'bg-red-500'
  else if (ratio >= USAGE_WARN_RATIO) barTone = 'bg-amber-500'

  return (
    <div className="min-w-[7rem]">
      <div className="text-[13px] text-gray-800">
        {r.dailyCallCount} <span className="text-gray-400">/</span> {r.dailyCap}
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${barTone}`}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
    </div>
  )
}

function reputationCell(r: PoolNumber): JSX.Element {
  let tone = 'text-gray-700'
  if (r.reputationScore >= 70) tone = 'text-emerald-700'
  else if (r.reputationScore < 40) tone = 'text-red-700'
  return <span className={`text-[13px] font-semibold ${tone}`}>{r.reputationScore}</span>
}

function statusCell(r: PoolNumber): JSX.Element {
  if (!r.disabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
        Active
      </span>
    )
  }
  return (
    <div className="min-w-0">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden="true" />
        Disabled
      </span>
      {r.disabledReason !== null && r.disabledReason !== '' && (
        <div className="mt-1 truncate text-xs text-gray-500" title={r.disabledReason}>
          {r.disabledReason}
        </div>
      )}
    </div>
  )
}

const columns: ColumnDef<PoolNumber>[] = [
  { label: 'Number', accessor: (r) => numberCell(r) },
  { label: 'Country', accessor: (r) => countryCell(r) },
  { label: 'Usage today', accessor: (r) => usageCell(r) },
  { label: 'Reputation', accessor: (r) => reputationCell(r) },
  { label: 'Status', accessor: (r) => statusCell(r) },
  {
    label: 'Created Date',
    accessor: (r) => <span className="text-gray-600">{formatDateTime(r.createdAt)}</span>,
  },
]

function NumberPoolPageInner(): JSX.Element {
  return (
    <AdminListScaffold<PoolNumber>
      title="Outbound Caller ID"
      description="Numbers presented on outbound campaign calls. The dialer picks from this pool per destination country, honouring daily caps and reputation."
      endpoint="/admin/number-pool"
      responseKey="numbers"
      addLabel="Add Caller ID"
      tableTitle="Caller ID Pool"
      searchPlaceholder="Search number, country or reason…"
      searchAriaLabel="Search caller IDs"
      emptyIcon="pi pi-phone"
      emptyDescription="Add at least one number per destination country you dial."
      entityLabel="caller ID"
      columns={columns}
      totalFrom={(s) => s['total'] ?? 0}
      totalLabel="Total numbers"
      totalIcon="pi pi-list"
      tiles={(summary) => (
        <SummaryCards
          cards={[
            { label: 'Total numbers', value: summary?.['total'] ?? 0, accentColor: '#6366f1' },
            { label: 'In rotation', value: summary?.['totalActive'] ?? 0, accentColor: '#10b981' },
            { label: 'At daily cap', value: summary?.['totalCapped'] ?? 0, accentColor: '#f59e0b' },
          ]}
        />
      )}
      deleteMessage={(r) =>
        `Remove ${r.phoneNumber} from the caller-ID pool? Calls will no longer present this number.`
      }
      renderDrawer={({ open, item, onClose, onSaved }) => (
        <NumberPoolDrawer
          open={open}
          value={item}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    />
  )
}

export default function NumberPoolPage(): JSX.Element {
  return (
    <RequirePermission permission="admin:extensions:view">
      <NumberPoolPageInner />
    </RequirePermission>
  )
}
